/**
 * Region Form Drawer
 * Create/Edit form for regions with country and factory selection
 */

import { useEffect } from 'react'
import { useTranslation, useForm, Controller, zodResolver, z } from '@spcc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Switch,
  Checkbox,
  cn,
  MapPin,
  Save,
  X,
  Factory,
} from '@spcc/uikit'
import type { Region, FactorySummary, CountrySummary } from '../../domain/types'
import { T_KEYS } from '../../domain/constants'

// ============================================================================
// Types
// ============================================================================

export interface RegionFormDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  region?: Region | null
  countries: CountrySummary[]
  factories: FactorySummary[]
  onSubmit: (data: RegionFormSubmitData) => void
  isLoading?: boolean
  isRTL?: boolean
}

export interface RegionFormSubmitData {
  code: string
  name: string
  description?: string
  isActive: boolean
  countryId?: string
  factoryIds: string[]
}

// ============================================================================
// Schema & Types
// ============================================================================

interface RegionFormData {
  code: string
  name: string
  description: string
  isActive: boolean
  countryId: string
  factoryIds: string[]
}

const regionFormSchema = z.object({
  code: z
    .string()
    .min(1, T_KEYS.REGIONS.VALIDATION.CODE_REQUIRED)
    .max(50, 'Code must be 50 characters or less'),
  name: z.string().min(1, T_KEYS.REGIONS.VALIDATION.NAME_REQUIRED),
  description: z.string().default(''),
  isActive: z.boolean().default(true),
  countryId: z.string().default(''),
  factoryIds: z.array(z.string()).default([]),
})

// ============================================================================
// Utility Functions
// ============================================================================

function generateRegionCode(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 4).toUpperCase()
  return `REG-${timestamp}-${random}`
}

const NO_SELECTION_VALUE = '__none__'

function getDefaultValues(region?: Region | null): RegionFormData {
  if (region) {
    return {
      code: region.code,
      name: region.name,
      description: region.description || '',
      isActive: region.isActive,
      countryId: region.country?.documentId || NO_SELECTION_VALUE,
      factoryIds: region.factories?.map((f) => f.documentId) || [],
    }
  }
  return {
    code: generateRegionCode(),
    name: '',
    description: '',
    isActive: true,
    countryId: NO_SELECTION_VALUE,
    factoryIds: [],
  }
}

function formDataToSubmitData(data: RegionFormData): RegionFormSubmitData {
  const countryId = data.countryId === NO_SELECTION_VALUE ? undefined : data.countryId

  return {
    code: data.code.trim().toUpperCase(),
    name: data.name.trim(),
    description: data.description.trim() || undefined,
    isActive: data.isActive,
    countryId,
    factoryIds: data.factoryIds,
  }
}

// ============================================================================
// Component
// ============================================================================

export function RegionFormDrawer({
  open,
  onOpenChange,
  region,
  countries,
  factories,
  onSubmit,
  isLoading = false,
  isRTL = false,
}: RegionFormDrawerProps) {
  const { t } = useTranslation()
  const isEditing = !!region

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegionFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(regionFormSchema) as any,
    defaultValues: getDefaultValues(region),
  })

  const selectedFactoryIds = watch('factoryIds')

  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      reset(getDefaultValues(region))
    }
  }, [open, region?.documentId, reset, region])

  const onFormSubmit = (data: RegionFormData) => {
    onSubmit(formDataToSubmitData(data))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSave = handleSubmit(onFormSubmit as any)

  const handleFactoryToggle = (factoryDocId: string, checked: boolean) => {
    const currentIds = selectedFactoryIds || []
    if (checked) {
      setValue('factoryIds', [...currentIds, factoryDocId])
    } else {
      setValue('factoryIds', currentIds.filter((id) => id !== factoryDocId))
    }
  }

  const headerContent = (
    <div className={cn('flex items-center gap-3')}>
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <MapPin className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-white">
          {isEditing ? t('regions.editRegion') : t('regions.addRegion')}
        </h2>
        <p className="text-sm text-white/80">
          {isEditing
            ? t('regions.editRegionDescription')
            : t('regions.addRegionDescription')}
        </p>
      </div>
    </div>
  )

  const footerContent = (
    <div className={cn('flex gap-3')}>
      <Button
        type="button"
        variant="outline"
        className="flex-1 h-12 rounded-xl"
        onClick={() => onOpenChange(false)}
        disabled={isLoading}
      >
        <X className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {t('common.cancel')}
      </Button>
      <Button
        type="button"
        className="flex-1 h-12 rounded-xl"
        disabled={isLoading}
        onClick={handleSave}
      >
        <Save className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {isLoading ? t('common.saving') : t('common.save')}
      </Button>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
      footerContent={footerContent}
      maxWidth="sm:max-w-lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <form id="region-form" onSubmit={handleSubmit(onFormSubmit as any)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
          </h3>

          {/* Code */}
          <div className="space-y-2">
            <Label htmlFor="code">{t('regions.code')} *</Label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="code"
                    value={value ?? ''}
                    placeholder={t('regions.codePlaceholder')}
                    className={cn('h-11 rounded-xl uppercase', errors.code && 'border-destructive')}
                    dir="ltr"
                    disabled={isEditing}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.code && <p className="text-xs text-destructive">{t(errors.code.message || '')}</p>}
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('regions.name')} *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id="name"
                    value={value ?? ''}
                    placeholder={t('regions.namePlaceholder')}
                    className={cn('h-11 rounded-xl', errors.name && 'border-destructive')}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.name && <p className="text-xs text-destructive">{t(errors.name.message || '')}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('regions.description')}</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Textarea
                    id="description"
                    value={value ?? ''}
                    placeholder={t('regions.descriptionPlaceholder')}
                    className="min-h-[80px] rounded-xl"
                    {...fieldProps}
                  />
                )
              }}
            />
          </div>

          {/* Status Toggle */}
          <div className="flex items-center justify-between py-2">
            <Label htmlFor="isActive" className="cursor-pointer">
              {t('regions.status')}
            </Label>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <span className={cn('text-sm', !field.value && 'text-muted-foreground')}>
                    {field.value ? t('regions.active') : t('regions.inactive')}
                  </span>
                  <Switch
                    id="isActive"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>
        </div>

        {/* Country Selection */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {isRTL ? 'البلد' : 'Country'}
          </h3>

          <div className="space-y-2">
            <Label>{t('regions.country')}</Label>
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder={t('regions.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SELECTION_VALUE}>
                      {t('common.none', 'None')}
                    </SelectItem>
                    {countries.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {t('common.loading')}
                      </div>
                    ) : (
                      countries.map((country) => (
                        <SelectItem key={country.id} value={country.documentId}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-muted-foreground">
                              [{country.code}]
                            </span>
                            <span>{country.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Factory Assignment */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('regions.factories')}
          </h3>

          <div className="space-y-2">
            <Label>{t('regions.selectFactories')}</Label>
            <div className="border rounded-xl p-3 max-h-[200px] overflow-y-auto space-y-2">
              {factories.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {isRTL ? 'لا توجد مصانع متاحة' : 'No factories available'}
                </div>
              ) : (
                factories
                  .filter((f) => f.status === 'active')
                  .map((factory) => {
                    const isSelected = selectedFactoryIds?.includes(factory.documentId)
                    return (
                      <div
                        key={factory.documentId}
                        className={cn(
                          'flex items-center gap-3 p-2 rounded-lg transition-colors',
                          isSelected ? 'bg-primary/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        )}
                      >
                        <Checkbox
                          id={`factory-${factory.documentId}`}
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleFactoryToggle(factory.documentId, checked === true)
                          }
                        />
                        <label
                          htmlFor={`factory-${factory.documentId}`}
                          className="flex items-center gap-2 cursor-pointer flex-1"
                        >
                          <Factory className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{factory.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">
                            ({factory.code})
                          </span>
                        </label>
                      </div>
                    )
                  })
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedFactoryIds?.length || 0} {isRTL ? 'مصنع محدد' : 'factories selected'}
            </p>
          </div>
        </div>
      </form>
    </BaseDrawer>
  )
}
